import {SortingState} from '@tanstack/table-core';

export const COLUMN_IDS = {
  post: 'post',
  creator: 'creator',
  followers: 'followers',
  impressions: 'impressions',
  comments: 'comments',
  likes: 'likes',
  saves: 'saves',
  views: 'views',
  avgWatchTime: 'avgWatchTime',
  videoViewsOrganic: 'videoViewsOrganic',
  videoViewsPaid: 'videoViewsPaid',
  shares: 'shares',
  engagementRate: 'engagementRate',
} as const;

export type ColumnId = (typeof COLUMN_IDS)[keyof typeof COLUMN_IDS];

export const COLUMN_SIZES: Record<ColumnId, number> = {
  [COLUMN_IDS.post]: 80,
  [COLUMN_IDS.creator]: 180,
  [COLUMN_IDS.followers]: 100,
  [COLUMN_IDS.impressions]: 110,
  [COLUMN_IDS.comments]: 100,
  [COLUMN_IDS.likes]: 80,
  [COLUMN_IDS.saves]: 80,
  [COLUMN_IDS.views]: 80,
  [COLUMN_IDS.avgWatchTime]: 120,
  [COLUMN_IDS.videoViewsOrganic]: 110,
  [COLUMN_IDS.videoViewsPaid]: 100,
  [COLUMN_IDS.shares]: 80,
  [COLUMN_IDS.engagementRate]: 140,
};

// Single source of truth: Maps table column IDs (camelCase) to API field names (snake_case)
export const COLUMN_ID_TO_API_FIELD: Record<ColumnId, string> = {
  [COLUMN_IDS.post]: 'posted_at',
  [COLUMN_IDS.creator]: '',
  [COLUMN_IDS.followers]: 'follower_count',
  [COLUMN_IDS.impressions]: 'impression_count',
  [COLUMN_IDS.comments]: 'comment_count',
  [COLUMN_IDS.likes]: 'like_count',
  [COLUMN_IDS.saves]: 'saved_count',
  [COLUMN_IDS.views]: 'view_count',
  [COLUMN_IDS.avgWatchTime]: 'video_average_watch_time',
  [COLUMN_IDS.videoViewsOrganic]: 'video_views_organic',
  [COLUMN_IDS.videoViewsPaid]: 'video_views_paid',
  [COLUMN_IDS.shares]: 'share_count',
  [COLUMN_IDS.engagementRate]: 'engagement_rate',
};

// Reverse mapping: API field -> table column ID
export const API_FIELD_TO_COLUMN_ID: Record<string, ColumnId> =
  Object.fromEntries(
    Object.entries(COLUMN_ID_TO_API_FIELD)
      .filter(([, apiField]) => apiField !== '')
      .map(([columnId, apiField]) => [apiField, columnId as ColumnId])
  );

export const DEFAULT_SORT = '-posted_at';

const POSTS_SORTABLE_API_FIELDS = new Set([
  'posted_at',
  ...Object.values(COLUMN_ID_TO_API_FIELD).filter((field) => field !== ''),
]);

export function normalizePostsSort(sort: string | null): string {
  if (!sort) {
    return DEFAULT_SORT;
  }

  const field = sort.startsWith('-') ? sort.slice(1) : sort;
  if (!POSTS_SORTABLE_API_FIELDS.has(field)) {
    return DEFAULT_SORT;
  }

  return sort;
}

// Parse sort string (e.g., '-posted_at' or 'posted_at') to SortingState
export function parseSortString(sortStr: string): SortingState {
  if (!sortStr) {
    return [];
  }

  const isDesc = sortStr.startsWith('-');
  const apiField = isDesc ? sortStr.slice(1) : sortStr;
  const columnId = API_FIELD_TO_COLUMN_ID[apiField] || (apiField as ColumnId);
  return [{id: columnId, desc: isDesc}];
}
